import { useMemo } from 'react';
import useViewProps from '../useViewProps';
import useViewSwitcherProps from '../useViewSwitcherProps';
import useTodoListProps from '../useTodoListProps';
import useAugmentedViewProps from '../useAugmentedViewProps';

/**
 * Assembles base/augmented view props and view-switcher props for SprintDetail.
 */
const useAssembledViewProps = ({
  core,
  setup,
  state,
  nested,
  composed,
  campaignViews,
  fokusModeRefs,
  deliverables,
  viewOptions,
  undoRedo,
}) => {
  const {
    localSprint,
    setLocalSprint,
    actions,
    recordAction,
    wrapLastUndoAction,
    isMultiCampaignMode,
    allSprints,
    isTemplate,
    settings,
    onSelectSprint,
    onUpdateSprint,
    onSprintIconChange,
    onNestCampaign,
    loadFullSprint,
    activeView,
    multiCampaignData,
    onRemoveCampaignFromView,
    onAddCampaignToView,
    removingCampaignId,
    hideViewExplainerModal,
    onHideViewExplainerModalChange,
    effectiveFlowViewVisibility,
    modals,
    wrappedToolbar,
  } = core;

  const {
    tasks,
    statuses,
    cardDisplaySettingsPerView,
    swimlaneSetting,
    sortSettingsPerView,
    allTags,
    archivedCount,
  } = setup;

  const {
    flowchartRef,
    filtersPerView,
    showArchived,
    showActionableOnly,
    showNestedCampaigns,
    todoViewMode,
    setTodoViewMode,
    todoSwimlaneGroupBy,
    setTodoSwimlaneGroupBy,
    todoSwimlane,
    setTodoSwimlane,
    todoShowNestedItems,
    setTodoShowNestedItems,
    tableSwimlane,
    setTableSwimlane,
    todoSelectedIds,
    setTodoSelectedIds,
    tableColumnVisibility,
    todoElementVisibility,
    setIsArtifactSummaryOpen,
    setIsHistoryOpen,
    isHistoryOpen,
    setIsCampaignPickerOpen,
    editingKanbanColumnId,
    setEditingKanbanColumnId,
    detailSidebarTaskId,
    setDetailSidebarTaskId,
    attachmentsModalTaskId,
    setAttachmentsModalTaskId,
    deliverablesModalTaskId,
    setDeliverablesModalTaskId,
    todoModalTriggerRef,
  } = state;

  const {
    handlers,
    handleViewRename,
    handleViewIconChangeLocal,
    openCampaignPickerForPaste,
    handleNestTasks,
    handleCompileMissionsIntoNestedCampaign,
    handleExplodeNestedCampaign,
    calculatedProgress,
    handleTrashNestedSprint,
    crossSprintActions,
    effectiveHandlers,
    wrappedActions,
    isLoadingNested,
  } = composed;

  const { kanbanFokus, tableFokus, ganttFokus, todoFokus, neuralFokus } = fokusModeRefs;
  const { handleSubmitDeliverable, handleEditDeliverables, handleClearDeliverable } = deliverables;
  const {
    isTidyToolbarBusy,
    setIsTidyToolbarBusy,
    handleManageCustomFields,
    onResetCampaignProgress,
  } = viewOptions;
  const { undo, redo, canUndo, canRedo, undoStack, redoStack } = undoRedo;

  const customFields = useMemo(() => localSprint?.customFields || [], [localSprint?.customFields]);

  const {
    flowProps: baseFlowProps,
    kanbanProps: baseKanbanProps,
    tableProps: baseTableProps,
    handleAddKanbanColumn,
    handleConfirmDeleteTaskWithNested,
    handleDeleteTasksWithNestedCheck,
    flowFilteredTasks,
    neuralFilteredTasks,
  } = useViewProps({
    flowchartRef, localSprint, setLocalSprint, tasks, statuses,
    calculatedProgress, editingKanbanColumnId, setEditingKanbanColumnId,
    detailSidebarTaskId, setDetailSidebarTaskId,
    attachmentsModalTaskId, setAttachmentsModalTaskId,
    deliverablesModalTaskId, setDeliverablesModalTaskId,
    isTemplate, settings,
    cardDisplaySettingsPerView, swimlaneSetting, sortSettingsPerView, filtersPerView,
    showArchived, showActionableOnly, showNestedCampaigns, actions: wrappedActions, handlers, modals, nested, toolbar: wrappedToolbar,
    handleNestTasks, handleCompileMissionsIntoNestedCampaign, handleExplodeNestedCampaign, handleTrashNestedSprint, onSelectSprint,
    onSprintIconChange: setup.handleLocalSprintIconChange, onAnySprintIconChange: onSprintIconChange,
    onNestCampaign,
    flowViewVisibility: effectiveFlowViewVisibility,
    onFlowViewVisibilityChange: effectiveHandlers.effectiveOnFlowViewVisibilityChange,
    hideViewExplainerModal, onHideViewExplainerModalChange,
    tableColumnVisibility,
    tableSwimlane,
    tableSwimlaneContext: {
      allTags,
      nestedSprints: nested.nestedSprints || [],
      hasPriorityField: false,
      hasAssigneeField: false,
      hasDueDateField: false,
    },
    recordAction,
    wrapLastUndoAction,
    currentView: wrappedToolbar.currentView,
    customViews: campaignViews.customViews,
    activeCustomViewId: campaignViews.activeCustomViewId,
    onViewChange: wrappedToolbar.setCurrentView,
    onSelectCustomView: campaignViews.selectView,
    openCampaignPickerForPaste,
    handleSubmitDeliverable,
    handleEditDeliverables,
    handleClearDeliverable,
    customFields,
    isMultiCampaignMode,
    allSprints,
    loadFullSprint,
    onTidyToolbarBusyChange: setIsTidyToolbarBusy,
  });

  const todoListRenameHandler = isMultiCampaignMode ? handleViewRename : handlers.handleRenameSprint;
  const todoListIconHandler = isMultiCampaignMode ? handleViewIconChangeLocal : setup.handleLocalSprintIconChange;

  const baseTodoListProps = useTodoListProps({
    tasks, statuses, localSprint, calculatedProgress, allSprints, allTags,
    nested, loadFullSprint, actions: wrappedActions, crossSprintActions, handlers,
    onSelectSprint, handleLocalSprintIconChange: todoListIconHandler, handleRenameSprint: todoListRenameHandler,
    setDetailSidebarTaskId, setAttachmentsModalTaskId, showArchived, showActionableOnly,
    todoViewMode, setTodoViewMode,
    todoSwimlaneGroupBy, setTodoSwimlaneGroupBy,
    todoSwimlane, todoShowNestedItems,
    todoModalTriggerRef,
    todoSelectedIds, setTodoSelectedIds,
    todoFilters: filtersPerView?.todolist || {},
    todoSortBy: sortSettingsPerView?.todolist || 'manual',
    todoElementVisibility,
    currentView: wrappedToolbar.currentView,
    customViews: campaignViews.customViews,
    activeCustomViewId: campaignViews.activeCustomViewId,
    onViewChange: wrappedToolbar.setCurrentView,
    onSelectCustomView: campaignViews.selectView,
    onUpdateSprint,
    isLoadingNested,
  });

  const { flowProps, augmentedKanbanProps, augmentedTableProps, todoListProps } = useAugmentedViewProps({
    baseFlowProps, baseKanbanProps, baseTableProps, baseTodoListProps,
    isMultiCampaignMode, activeView, multiCampaignData,
    kanbanFokus, tableFokus, handleViewRename, handleViewIconChangeLocal,
    onRemoveCampaignFromView,
    onAddCampaignToView,
    removingCampaignId,
  });

  const viewSwitcherProps = useViewSwitcherProps({
    toolbar: wrappedToolbar, flowchartRef, tasks, statuses, sprint: localSprint, localSprint, nested, allSprints,
    cardDisplaySettingsPerView, swimlaneSetting, sortSettingsPerView, filtersPerView,
    showArchived, archivedCount, allTags,
    todoViewMode, setTodoViewMode: effectiveHandlers.effectiveSetTodoViewMode,
    todoSwimlaneGroupBy, setTodoSwimlaneGroupBy: effectiveHandlers.effectiveSetTodoSwimlaneGroupBy,
    todoSwimlane, setTodoSwimlane,
    todoShowNestedItems, setTodoShowNestedItems,
    actions, handlers, undo, redo, canUndo, canRedo, undoStack, redoStack,
    setIsArtifactSummaryOpen, setIsHistoryOpen, isHistoryOpen,
    handleFiltersChange: effectiveHandlers.effectiveHandleFiltersChange,
    handleAddKanbanColumn,
    handleCardDisplaySettingsChange: effectiveHandlers.effectiveHandleCardDisplaySettingsChange,
    handleSwimlaneSettingChange: effectiveHandlers.effectiveHandleSwimlaneSettingChange,
    handleSortSettingChange: effectiveHandlers.effectiveHandleSortSettingChange,
    handleToggleArchived: effectiveHandlers.effectiveHandleToggleArchived,
    showActionableOnly,
    handleToggleActionableOnly: effectiveHandlers.effectiveHandleToggleActionableOnly,
    showNestedCampaigns,
    setShowNestedCampaigns: effectiveHandlers.effectiveSetShowNestedCampaigns,
    todoModalTriggerRef, isMultiCampaignMode, activeView, multiCampaignCampaigns: multiCampaignData?.campaigns || [],
    onOpenCampaignPicker: () => setIsCampaignPickerOpen(true),
    flowViewVisibility: effectiveFlowViewVisibility,
    onFlowViewVisibilityChange: effectiveHandlers.effectiveOnFlowViewVisibilityChange,
    kanbanFokus, tableFokus, ganttFokus, todoFokus, neuralFokus,
    tableColumnVisibility,
    onTableColumnVisibilityChange: effectiveHandlers.effectiveSetTableColumnVisibility,
    todoElementVisibility,
    onTodoElementVisibilityChange: effectiveHandlers.effectiveSetTodoElementVisibility,
    ganttSwimlane: wrappedToolbar.ganttSwimlane,
    setGanttSwimlane: effectiveHandlers.effectiveSetGanttSwimlane,
    tableSwimlane,
    setTableSwimlane,
    customViews: campaignViews.customViews,
    activeCustomViewId: campaignViews.activeCustomViewId,
    onAddCustomView: campaignViews.addView,
    onSelectCustomView: campaignViews.selectView,
    onRenameCustomView: campaignViews.renameView,
    onDuplicateCustomView: campaignViews.duplicateView,
    onDeleteCustomView: campaignViews.deleteView,
    onReorderViews: campaignViews.reorderViews,
    customFields,
    onManageCustomFields: handleManageCustomFields,
    isTidyToolbarBusy,
    onResetCampaignProgress,
  });

  return {
    flowFilteredTasks,
    neuralFilteredTasks,
    handleAddKanbanColumn,
    handleConfirmDeleteTaskWithNested,
    handleDeleteTasksWithNestedCheck,
    flowProps,
    augmentedKanbanProps,
    augmentedTableProps,
    todoListProps,
    viewSwitcherProps,
  };
};

export default useAssembledViewProps;
